// P1-005: Agent Zustand store — collection, formation, gacha, leveling
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AgentType, ElementType, SkillEffect } from '@/shared/types/common';
import { AgentStatCalc } from '@/shared/formulas/agent-stat-calc';
import { usePlayerStore } from '@/shared/stores/playerStore';
import {
  AGENT_CONFIGS,
  STARTER_AGENT_IDS,
  STARTER_LEVEL,
  GACHA_RATES,
  GACHA_SINGLE_COST,
  GACHA_TEN_COST,
  GACHA_PITY_THRESHOLD,
  getAgentsByRarity,
} from '../data/agent-configs';
import type { AgentConfig, AgentSkillConfig } from '../data/agent-configs';

// ─────────────────────────────────────────────────────
// Runtime Agent types (owned instance with level, exp, etc.)
// ─────────────────────────────────────────────────────

export interface AgentSkillRuntime {
  skillId: string;
  name: string;
  description: string;
  cooldown: number;
  effects: SkillEffect[];
}

export interface OwnedAgent {
  /** Unique instance ID */
  instanceId: string;
  /** Reference to AgentConfig.id */
  configId: string;
  name: string;
  type: AgentType;
  rarity: 2 | 3 | 4 | 5 | 6;
  level: number;
  /** Calculated ATK at current level */
  attack: number;
  /** Calculated DEF at current level */
  defense: number;
  /** Calculated HP at current level */
  hp: number;
  /** Calculated compute cost at current level */
  computeCost: number;
  /** Crit rate (percent) */
  critRate: number;
  elementAffinity: ElementType;
  activeSkill: AgentSkillRuntime;
  /** Base stats for recalculation */
  baseATK: number;
  baseDEF: number;
  baseHP: number;
  baseComputeCost: number;
  /** AI 生成的立绘 blob URL (来自 IndexedDB) */
  generatedPortraitUrl?: string;
  /** 召唤时玩家输入的许愿文本 */
  wishText?: string;
  /** 生成立绘时使用的服装提示词 */
  costumePrompt?: string;
  /** 召唤时间戳 */
  summonedAt?: number;
}

// ─────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────

let agentInstanceCounter = 0;

function createOwnedAgent(
  config: AgentConfig,
  level: number,
  extras?: { wishText?: string; costumePrompt?: string; summonedAt?: number },
): OwnedAgent {
  agentInstanceCounter += 1;
  return recalcStats({
    instanceId: `${config.id}-${agentInstanceCounter}`,
    configId: config.id,
    name: config.name,
    type: config.type,
    rarity: config.rarity,
    level,
    attack: 0,
    defense: 0,
    hp: 0,
    computeCost: 0,
    critRate: 0,
    elementAffinity: config.elementAffinity,
    activeSkill: skillConfigToRuntime(config.activeSkill),
    baseATK: config.baseATK,
    baseDEF: config.baseDEF,
    baseHP: config.baseHP,
    baseComputeCost: config.baseComputeCost,
    ...extras,
  });
}

function recalcStats(agent: OwnedAgent): OwnedAgent {
  return {
    ...agent,
    attack: AgentStatCalc.calcAttack(agent.baseATK, agent.level),
    defense: AgentStatCalc.calcDefense(agent.baseDEF, agent.level),
    hp: AgentStatCalc.calcHP(agent.baseHP, agent.level),
    computeCost: AgentStatCalc.calcComputeCost(agent.baseComputeCost, agent.rarity, agent.level),
    critRate: AgentStatCalc.calcCritRate(agent.rarity),
  };
}

function skillConfigToRuntime(skill: AgentSkillConfig): AgentSkillRuntime {
  return {
    skillId: skill.skillId,
    name: skill.name,
    description: skill.description,
    cooldown: skill.cooldown,
    effects: [...skill.effects],
  };
}

/** Weighted random pick from gacha rates → returns rarity (2-6) */
function rollGachaRarity(pityCount: number): 2 | 3 | 4 | 5 | 6 {
  // Pity: if pityCount >= threshold, guarantee 4-star or above
  if (pityCount >= GACHA_PITY_THRESHOLD) {
    // Remove 2-star and 3-star from pool, redistribute
    const highRates = [0, 0, GACHA_RATES[2]!, GACHA_RATES[3]!, GACHA_RATES[4]!];
    const total = highRates.reduce((a, b) => a + b, 0);
    const roll = Math.random() * total;
    let cumulative = 0;
    for (let i = 0; i < highRates.length; i++) {
      cumulative += highRates[i]!;
      if (roll < cumulative) {
        return (i + 2) as 2 | 3 | 4 | 5 | 6;
      }
    }
    return 4; // fallback
  }

  const roll = Math.random() * 100;
  let cumulative = 0;
  for (let i = 0; i < GACHA_RATES.length; i++) {
    cumulative += GACHA_RATES[i]!;
    if (roll < cumulative) {
      return (i + 2) as 2 | 3 | 4 | 5 | 6;
    }
  }
  return 2; // fallback
}

function pickRandomAgent(rarity: number): AgentConfig | undefined {
  const pool = getAgentsByRarity(rarity);
  if (pool.length === 0) return undefined;
  return pool[Math.floor(Math.random() * pool.length)];
}

// ─────────────────────────────────────────────────────
// State interface
// ─────────────────────────────────────────────────────

export interface GachaResult {
  agent: OwnedAgent;
  isNew: boolean;
  /** 立绘生成状态 */
  portraitStatus: 'pending' | 'generating' | 'success' | 'fallback' | 'error';
}

interface AgentState {
  ownedAgents: OwnedAgent[];
  formation: (OwnedAgent | null)[];
  pityCounter: number;

  // Mutations
  addAgent: (agent: OwnedAgent) => void;
  removeFromFormation: (slotIndex: number) => void;
  setFormationSlot: (slotIndex: number, agentInstanceId: string) => boolean;
  levelUp: (agentInstanceId: string) => boolean;
  drawGacha: (type: 'single' | 'ten') => GachaResult[] | null;

  // Getters
  getFormation: () => (OwnedAgent | null)[];
  getFormationAgents: () => OwnedAgent[];
}

// ─────────────────────────────────────────────────────
// Create initial state: 5 starters at Lv5, auto-deployed
// ─────────────────────────────────────────────────────

function createInitialAgentState(): {
  ownedAgents: OwnedAgent[];
  formation: (OwnedAgent | null)[];
} {
  const starters: OwnedAgent[] = [];
  for (const id of STARTER_AGENT_IDS) {
    const config = AGENT_CONFIGS.find((a) => a.id === id);
    if (config) {
      starters.push(createOwnedAgent(config, STARTER_LEVEL));
    }
  }

  // Formation: deploy all 5 starters
  const formation: (OwnedAgent | null)[] = [null, null, null, null, null];
  for (let i = 0; i < Math.min(starters.length, 5); i++) {
    formation[i] = starters[i] ?? null;
  }

  return { ownedAgents: starters, formation };
}

// ─────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────

export const useAgentStore = create<AgentState>()(
  persist(
    (set, get) => {
      const initial = createInitialAgentState();

      return {
        ownedAgents: initial.ownedAgents,
        formation: initial.formation,
        pityCounter: 0,

        addAgent: (agent) => {
          set((state) => ({ ownedAgents: [...state.ownedAgents, agent] }));
        },

        removeFromFormation: (slotIndex) => {
          set((state) => {
            const newFormation = [...state.formation];
            if (slotIndex >= 0 && slotIndex < 5) {
              newFormation[slotIndex] = null;
            }
            return { formation: newFormation };
          });
        },

        setFormationSlot: (slotIndex, agentInstanceId) => {
          const state = get();
          if (slotIndex < 0 || slotIndex >= 5) return false;

          const agent = state.ownedAgents.find((a) => a.instanceId === agentInstanceId);
          if (!agent) return false;

          // Check if already deployed in another slot
          const existingSlot = state.formation.findIndex(
            (a) => a?.instanceId === agentInstanceId
          );
          if (existingSlot !== -1) return false;

          const newFormation = [...state.formation];
          newFormation[slotIndex] = agent;
          set({ formation: newFormation });
          return true;
        },

        levelUp: (agentInstanceId) => {
          const state = get();
          const agent = state.ownedAgents.find((a) => a.instanceId === agentInstanceId);
          if (!agent) return false;

          const maxLvl = AgentStatCalc.maxLevel(agent.rarity);
          if (agent.level >= maxLvl) return false;

          // Cost = currentLevel * 50 FLOP (GDD SS4.8.2)
          const cost = agent.level * 50;
          const playerStore = usePlayerStore.getState();
          if (!playerStore.spendFlop(cost)) return false;

          const updatedAgent = recalcStats({ ...agent, level: agent.level + 1 });

          set({
            ownedAgents: state.ownedAgents.map((a) =>
              a.instanceId === agentInstanceId ? updatedAgent : a
            ),
            formation: state.formation.map((a) =>
              a?.instanceId === agentInstanceId ? updatedAgent : a
            ),
          });
          return true;
        },

        drawGacha: (type) => {
          const playerStore = usePlayerStore.getState();
          const cost = type === 'single' ? GACHA_SINGLE_COST : GACHA_TEN_COST;
          if (!playerStore.spendAGI(cost)) return null;

          const count = type === 'single' ? 1 : 10;
          const results: GachaResult[] = [];
          let pity = get().pityCounter;

          for (let i = 0; i < count; i++) {
            pity += 1;
            const rarity = rollGachaRarity(pity);

            // Reset pity on 4-star or above
            if (rarity >= 4) {
              pity = 0;
            }

            const config = pickRandomAgent(rarity);
            if (!config) continue;

            const newAgent = createOwnedAgent(config, 1);
            const state = get();
            const isNew = !state.ownedAgents.some((a) => a.configId === config.id);

            // Add to owned
            get().addAgent(newAgent);
            results.push({ agent: newAgent, isNew, portraitStatus: 'pending' });
          }

          set({ pityCounter: pity });
          return results;
        },

        // ─── Getters ──────────────────────────────────

        getFormation: () => get().formation,

        getFormationAgents: () =>
          get().formation.filter((a): a is OwnedAgent => a !== null),
      };
    },
    {
      name: 'silicon-valley-agents',
    }
  )
);

/** Reset agent instance counter (for tests) */
export function _resetAgentInstanceCounter(): void {
  agentInstanceCounter = 0;
}
